/**
 * Transform Step Executor — Data mapping/transformation in the pipeline
 *
 * Config:
 * - mappings: Array<{ from: string, to: string }> — rename/copy fields
 * - extract?: Array<{ path: string, as: string }> — extract nested values
 * - compute?: Array<{ name: string, expression: string }> — simple computations
 * - remove?: string[] — fields to remove from pipelineData
 */

import type { EngineStep, EngineExecutionContext, StepExecutionResult, StepExecutor } from '../types';

interface FieldMapping {
  from: string;
  to: string;
}

interface FieldExtraction {
  path: string;
  as: string;
}

interface FieldComputation {
  name: string;
  expression: string;
}

export const transformStepExecutor: StepExecutor = {
  type: 'transform',
  async execute(step: EngineStep, context: EngineExecutionContext): Promise<StepExecutionResult> {
    try {
      const output: Record<string, unknown> = {};

      // Mappings: rename/copy fields
      const mappings = (step.config.mappings as FieldMapping[]) || [];
      for (const mapping of mappings) {
        const value = resolveDataPath(mapping.from, context.pipelineData);
        if (value !== undefined) {
          output[mapping.to] = value;
        }
      }

      // Extractions: pull out nested values
      const extractions = (step.config.extract as FieldExtraction[]) || [];
      for (const extraction of extractions) {
        const value = resolveDataPath(extraction.path, context.pipelineData);
        if (value !== undefined) {
          output[extraction.as] = value;
        }
      }

      // Computations: simple string template evaluation
      const computations = (step.config.compute as FieldComputation[]) || [];
      for (const comp of computations) {
        output[comp.name] = interpolateExpression(comp.expression, context.pipelineData);
      }

      // Removals: mark fields for removal (set to undefined)
      const removals = (step.config.remove as string[]) || [];
      for (const field of removals) {
        output[field] = undefined;
      }

      return {
        success: true,
        output,
      };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Transform step failed',
      };
    }
  },
};

function resolveDataPath(path: string, data: Record<string, unknown>): unknown {
  const parts = path.split('.');
  let current: unknown = data;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

function interpolateExpression(expr: string, data: Record<string, unknown>): string {
  return expr.replace(/\{\{(.+?)\}\}/g, (_, path) => {
    const value = resolveDataPath(path.trim(), data);
    return value !== undefined ? String(value) : '';
  });
}
